import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
  InputAdornment,

  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Alert,
  Fade,
} from '@mui/material';

import {
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  HourglassEmpty as ProcessingIcon,
  CloudUpload as UploadIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { fetchAllProcesses, deleteProcess } from '../store/slices/processesSlice';
import { parseUTCDate, safeFormatDate, calculateDuration, formatDuration } from '../utils/dateUtils';
import { useSidebar } from '../contexts/SidebarContext';

interface Process {
  id: string;
  processid: string;
  name?: string;
  // Backend supports additional statuses (e.g. "accepted").
  // Keep this permissive to avoid runtime issues when backend adds/returns new statuses.
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'accepted' | string;
  user_id: string;
  original_filename?: string;
  file_size?: number;
  error_message?: string;
  created_at: string;
  completed_at?: string;
  user?: {
    id: string;
    username: string;
    email: string;
  };
  summaries?: Array<{
    id: string;
    title: string;
    category: string;
    status: string;
  }>;
}

const ProcessesPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const auth = useAppSelector((state) => state.auth);
  const { sidebarOpen } = useSidebar();
  
  const processes = useAppSelector((state: any) => state.processes.processes || []);
  const loading = useAppSelector((state: any) => state.processes.loading);
  const error = useAppSelector((state: any) => state.processes.error);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [processToDelete, setProcessToDelete] = useState<string | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedProcess, setSelectedProcess] = useState<Process | null>(null);
  const [sortColumn, setSortColumn] = useState<string>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const isActiveStatus = (status: string) => {
    // Only these should tick in real time. Any other status is considered terminal for duration purposes.
    return status === 'pending' || status === 'processing';
  };

  useEffect(() => {
    dispatch(fetchAllProcesses());
  }, [dispatch]);

  // Auto-refresh every 30 seconds so new processes from other pages appear promptly.
  useEffect(() => {
    const interval = setInterval(() => {
      dispatch(fetchAllProcesses());
    }, 30000);

    return () => clearInterval(interval);
  }, [dispatch]);

  const getSortValue = (process: Process, column: string) => {
    switch (column) {
      case 'status':
        return process.status;
      case 'file':
        return process.original_filename || process.name || '';
      case 'processid':
        return process.processid;
      case 'user':
        return process.user?.username || process.user?.email || '';
      case 'size':
        return process.file_size || 0;
      case 'duration':
        // Calculate duration from created_at to completed_at or current time
        let endDate: string | undefined;
        if (!isActiveStatus(process.status)) {
          // Use completed_at if it exists, otherwise use created_at for 0 duration
          endDate = process.completed_at || process.created_at;
        }
        return calculateDuration(process.created_at, endDate);
      case 'created_at':
        return parseUTCDate(process.created_at).getTime();
      default:
        return '';
    }
  };

  // Filter processes based on search term and status, then sort based on selected column and direction
  const filteredProcesses = processes.filter((process: Process) => {
    const matchesSearch = 
      process.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      process.original_filename?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      process.processid.toLowerCase().includes(searchTerm.toLowerCase()) ||
      process.user?.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      process.user?.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || process.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  }).sort((a: Process, b: Process) => {
    const aValue = getSortValue(a, sortColumn);
    const bValue = getSortValue(b, sortColumn);
    
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    
    return 0;
  });

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleDeleteClick = (processId: string) => {
    setProcessToDelete(processId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (processToDelete) {
      dispatch(deleteProcess(processToDelete));
    }
    setDeleteDialogOpen(false);
    setProcessToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setProcessToDelete(null);
  };

  const handleViewDetails = (process: Process) => {
    setSelectedProcess(process);
    setDetailsDialogOpen(true);
  };

  const handleRefresh = () => {
    dispatch(fetchAllProcesses());
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'accepted':
        return <CheckCircleIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      case 'processing':
        return <ProcessingIcon color="info" />;
      case 'pending':
        return <ScheduleIcon color="warning" />;
      default:
        return <ScheduleIcon />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'accepted':
        return 'success';
      case 'processing':
        return 'info';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'N/A';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getProcessDuration = (process: Process) => {
    // For completed or failed processes, use completed_at if available
    // For active processes (pending/processing), use current time
    let endDate: string | undefined;
    if (!isActiveStatus(process.status)) {
      // Use completed_at if it exists and is not empty, otherwise use created_at to show 0 duration
      endDate = process.completed_at && process.completed_at.trim() ? process.completed_at : process.created_at;
    }
    // If endDate is still undefined at this point for pending/processing, current time will be used

    const durationMs = calculateDuration(process.created_at, endDate);
    return formatDuration(durationMs);
  };

  return (
    <>
    <Fade in timeout={600}>
      <Box
        sx={{
          marginLeft: sidebarOpen ? '0px' : '0px',
          width: sidebarOpen ? 'calc(100% - 0px)' : 'calc(100% - 0px)',
          transition: 'margin-left 0.3s ease, width 0.3s ease',
          height: 'calc(100vh - 64px)',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="xl" sx={{ 
          mt: 4, 
          mb: 4,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}>
      {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 500 }}>
            Process Tracking
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading}
          aria-label="Refresh processes list"
        >
          Refresh
        </Button>
        </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
        <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search processes by name, file, or ID"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                inputProps={{
                  'aria-label': 'Search processes'
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  sx: { backgroundColor: 'white' }
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                select
                label="Status Filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                SelectProps={{
                  native: true,
                }}
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="text.secondary">
                Total: {filteredProcesses.length} processes
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
        </Card>

      {/* Processes Table */}
        {loading ? (
        <Box sx={{ width: '100%' }}>
          <LinearProgress />
        </Box>
      ) : filteredProcesses.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <UploadIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {searchTerm || statusFilter !== 'all' ? 'No matching processes found' : 'No processes yet'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {searchTerm || statusFilter !== 'all' 
                ? 'Try adjusting your search or filter criteria' 
                : 'Once you generate summaries, the processes will show here'}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>          <TableContainer component={Paper} sx={{
            flexGrow: 1,
            maxHeight: 'calc(100vh - 350px)',
            overflow: 'auto',
            // Custom scrollbar styling
            '&::-webkit-scrollbar': {
              width: '12px',
            },
            '&::-webkit-scrollbar-track': {
              background: (theme) => theme.palette.mode === 'dark' ? '#2a2a2a' : '#f1f3f4',
              borderRadius: '6px',
              margin: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: (theme) => theme.palette.mode === 'dark' ? '#555' : '#c1c1c1',
              borderRadius: '6px',
              border: '2px solid transparent',
              backgroundClip: 'content-box',
              '&:hover': {
                background: (theme) => theme.palette.mode === 'dark' ? '#777' : '#a8a8a8',
                backgroundClip: 'content-box',
              },
            },
            // Firefox scrollbar styling
            scrollbarWidth: 'thin',
            scrollbarColor: (theme) => theme.palette.mode === 'dark' ? '#555 #2a2a2a' : '#c1c1c1 #f1f3f4',
          }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell 
                    onClick={() => handleSort('status')}
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Status
                      {sortColumn === 'status' && (
                        sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell 
                    onClick={() => handleSort('file')}
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      File
                      {sortColumn === 'file' && (
                        sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell 
                    onClick={() => handleSort('processid')}
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Process ID
                      {sortColumn === 'processid' && (
                        sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  {auth.user?.role && ['admin', 'anis-admin', 'superadmin'].includes(auth.user.role) && (
                    <TableCell
                      onClick={() => handleSort('user')}
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        User
                        {sortColumn === 'user' && (
                          sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                        )}
                      </Box>
                    </TableCell>
                  )}
                  <TableCell 
                    onClick={() => handleSort('size')}
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Size
                      {sortColumn === 'size' && (
                        sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell 
                    onClick={() => handleSort('duration')}
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Duration
                      {sortColumn === 'duration' && (
                        sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell 
                    onClick={() => handleSort('created_at')}
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Created
                      {sortColumn === 'created_at' && (
                        sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredProcesses.map((process: Process) => (
                  <TableRow key={process.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={process.status}
                          color={getStatusColor(process.status) as any}
                          size="small"
                          icon={getStatusIcon(process.status)}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={process.name || process.original_filename || 'N/A'}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {process.name || process.original_filename || 'N/A'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={process.processid}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {process.processid.substring(0, 8)}...
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    {auth.user?.role && ['admin', 'anis-admin', 'superadmin'].includes(auth.user.role) && (
                      <TableCell>
                        <Typography variant="body2">
                          {process.user?.username || process.user?.email || 'N/A'}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="body2">
                        {formatFileSize(process.file_size)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {getProcessDuration(process)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {safeFormatDate(process.created_at, 'dd/MM, HH:mm')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetails(process)}
                            aria-label="View process details"
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        {((auth.user?.role && ['admin', 'anis-admin', 'superadmin'].includes(auth.user.role)) || process.user_id === auth.user?.id) && (
                          <Tooltip title="Delete Process">
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteClick(process.id)}
                                disabled={process.status === 'processing'}
                                aria-label="Delete process"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
        </Container>
      </Box>
    </Fade>

    {/* Process Details Dialog */}
    <Dialog
      open={detailsDialogOpen}
      onClose={() => setDetailsDialogOpen(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Process Details
      </DialogTitle>
      <DialogContent>
        {selectedProcess && (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Process ID
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', mb: 2 }}>
                  {selectedProcess.processid}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Status
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  {getStatusIcon(selectedProcess.status)}
                  <Chip
                    label={selectedProcess.status}
                    color={getStatusColor(selectedProcess.status) as any}
                    size="small"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Original Filename
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedProcess.original_filename || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  File Size
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {formatFileSize(selectedProcess.file_size)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Created At
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {safeFormatDate(selectedProcess.created_at, 'MMM dd, yyyy HH:mm')}
                </Typography>
              </Grid>
              {selectedProcess.completed_at && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Completed At
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {safeFormatDate(selectedProcess.completed_at, 'MMM dd, yyyy HH:mm')}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Duration
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {getProcessDuration(selectedProcess)}
                </Typography>
              </Grid>
              {auth.user?.role && ['admin', 'anis-admin', 'superadmin'].includes(auth.user.role) && selectedProcess.user && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    User
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedProcess.user.username} ({selectedProcess.user.email})
                  </Typography>
                </Grid>
              )}

              {selectedProcess.error_message && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Error Message
                  </Typography>
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {selectedProcess.error_message}
                  </Alert>
                </Grid>
              )}
              {selectedProcess.summaries && selectedProcess.summaries.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Generated Summaries ({selectedProcess.summaries.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedProcess.summaries.map((summary) => (
                      <Chip
                        key={summary.id}
                        label={summary.title}
                        variant="outlined"
                        size="small"
                        onClick={() => navigate(`/summaries?selected=${summary.id}`)}
                        clickable
                      />
                    ))}
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDetailsDialogOpen(false)} aria-label="Close process details">Close</Button>
      </DialogActions>
    </Dialog>

    {/* Delete Confirmation Dialog */}
    <Dialog
      open={deleteDialogOpen}
      onClose={handleDeleteCancel}
    >
      <DialogTitle>Delete Process</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to delete this process? This action cannot be undone and will also delete any associated summaries.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDeleteCancel}>Cancel</Button>
        <Button onClick={handleDeleteConfirm} color="error" autoFocus>
          Delete
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default ProcessesPage;